/**
 * Telnyx Number Verification Modal
 * Lets users verify phone numbers for live call testing.
 */

import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { Phone, ShieldCheck, Loader2 } from 'lucide-react';

const TELNYX_API_KEY = 'KEY019D5982E2B8FFFE4CEFBC54EA49367E_kJqFrpsILTecUzsyexki3O';

interface VerifyNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber?: string;
}

export function VerifyNumberModal({ isOpen, onClose, phoneNumber }: VerifyNumberModalProps) {
  const { success: showSuccess, error: showError } = useToast();
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [phone, setPhone] = useState(phoneNumber || '');
  const [method, setMethod] = useState<'sms' | 'call'>('sms');
  const [code, setCode] = useState('');
  const [pendingNumber, setPendingNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleRequest = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) {
      showError('Invalid', 'Enter a valid 10-digit phone number');
      return;
    }

    const fullNumber = `+1${digits}`;
    setSubmitting(true);

    try {
      const resp = await fetch('https://api.telnyx.com/v2/verified_numbers', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${TELNYX_API_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: fullNumber, verification_method: method }),
      });

      const data = await resp.json();
      if (data.errors) {
        showError('Error', data.errors[0]?.detail || 'Failed to start verification');
        setSubmitting(false);
        return;
      }

      setPendingNumber(fullNumber);
      setStep('confirm');
      showSuccess('Code sent', method === 'sms' ? `SMS sent to ${fullNumber}` : `Call placed to ${fullNumber}`);
    } catch (err: any) {
      showError('Error', err.message || 'Verification request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (code.length < 4) {
      showError('Invalid', 'Enter the verification code');
      return;
    }

    setSubmitting(true);
    try {
      const resp = await fetch(`https://api.telnyx.com/v2/verified_numbers/${encodeURIComponent(pendingNumber)}/actions/verify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${TELNYX_API_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification_code: code }),
      });

      const data = await resp.json();
      if (data.errors) {
        showError('Invalid code', 'The code is incorrect. Try again.');
        setSubmitting(false);
        return;
      }

      showSuccess('Verified!', `${pendingNumber} is now verified for live call testing.`);
      handleClose();
    } catch (err: any) {
      showError('Error', err.message || 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('request');
    setPhone(phoneNumber || '');
    setCode('');
    setPendingNumber('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Verify Phone Number" size="sm">
      {step === 'request' ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Verify this number to enable live caller ID testing across carriers.
          </p>
          <Input
            label="Phone Number"
            type="tel"
            placeholder="(555) 123-4567"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            leftIcon={<Phone className="h-4 w-4" />}
          />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Verification Method</label>
            <div className="flex gap-2">
              <button
                onClick={() => setMethod('sms')}
                className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${method === 'sms' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              >
                SMS
              </button>
              <button
                onClick={() => setMethod('call')}
                className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${method === 'call' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              >
                Phone Call
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button onClick={handleRequest} isLoading={submitting} leftIcon={<Phone className="h-4 w-4" />}>
              Send Code
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Enter the code sent to <span className="font-mono font-medium">{pendingNumber}</span>
          </p>
          <Input
            label="Verification Code"
            type="text"
            placeholder="Enter code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            leftIcon={<ShieldCheck className="h-4 w-4" />}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setStep('request')}>Back</Button>
            <Button onClick={handleConfirm} isLoading={submitting} leftIcon={<ShieldCheck className="h-4 w-4" />}>
              Verify
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
